<template>
  <div style="position: relative; height: 40vh; width: 80vw">
    <canvas id="chart"></canvas>
  </div>
</template>

<script>
import { Chart, LinearScale } from 'chart.js';
import { SankeyController, Flow } from 'chartjs-chart-sankey';

Chart.register(LinearScale, SankeyController, Flow);
export default {
  props: {
    emailCampaign: {
      type: Object,
      required: true,
    },
  },
  computed: {
    sent() {
      return Math.ceil(
        100 *
          (this.emailCampaign.email_addresses_sent_count / this.emailCampaign.email_addresses_count)
      );
    },
    pending() {
      return Math.ceil(
        100 *
          (this.emailCampaign.email_addresses_pending_count /
            this.emailCampaign.email_addresses_count)
      );
    },
    failed() {
      return Math.ceil(
        100 *
          (this.emailCampaign.email_addresses_failed_count /
            this.emailCampaign.email_addresses_count)
      );
    },
    sentOpened() {
      if (!this.emailCampaign.email_addresses_sent_count) {
        return 0;
      }

      return Math.ceil(
        100 * (this.emailCampaign.unique_open_count / this.emailCampaign.email_addresses_sent_count)
      );
    },
    sentBounced() {
      if (!this.emailCampaign.email_addresses_sent_count) {
        return 0;
      }

      return Math.ceil(
        100 * (this.emailCampaign.bounced_count / this.emailCampaign.email_addresses_sent_count)
      );
    },
    openedUnsubscribed() {
      if (!this.emailCampaign.unique_open_count) {
        return 0;
      }

      return Math.ceil(
        100 * (this.emailCampaign.unsubscribed_count / this.emailCampaign.unique_open_count)
      );
    },
    openedClicked() {
      if (!this.emailCampaign.unique_clicks_count) {
        return 0;
      }

      return Math.ceil(
        100 * (this.emailCampaign.unique_clicks_count / this.emailCampaign.unique_open_count)
      );
    },
    sentUnread() {
      return this.sent - this.sentBounced - this.sentOpened;
    },
    unreadCount() {
      return (
        this.emailCampaign.email_addresses_sent_count -
        this.emailCampaign.bounced_count -
        this.emailCampaign.unique_open_count
      );
    },
    flows() {
      return {
        sent: this.sent,
        pending: this.pending,
        failed: this.failed,
        sentOpened: this.sentOpened,
        sentUnread: this.sentUnread,
        sentBounced: this.sentBounced,
        openedUnsubscribed: this.openedUnsubscribed,
        openedClicked: this.openedClicked,
      };
    },
  },
  mounted() {
    const ctx = document.getElementById('chart').getContext('2d');

    var colors = {
      Recipients: '#3b82f6',
      Sent: '#06b6d4',
      Pending: '#f97316',
      'Send Failed': '#ef4444',
      Opened: '#10b981',
      Unread: '#64748b',
      Bounced: '#ef4444',
      Unsubscribed: '#0f172a',
      Clicked: '#22c55e',
    };

    const labels = {
      Recipients: `Recipients (${this.emailCampaign.email_addresses_count})`,
      Sent: `Sent (${this.emailCampaign.email_addresses_sent_count})`,
      Pending: `Pending (${this.emailCampaign.email_addresses_pending_count})`,
      'Send Failed': `Send Failed (${this.emailCampaign.email_addresses_failed_count})`,
      Opened: `Opened (${this.emailCampaign.unique_open_count})`,
      Unread: `Unread (${this.unreadCount})`,
      Bounced: `Bounced (${this.emailCampaign.bounced_count})`,
      Unsubscribed: `Unsubscribed (${this.emailCampaign.unsubscribed_count})`,
      Clicked: `Clicked (${this.emailCampaign.unique_clicks_count})`,
    };

    function getColor(name) {
      return colors[name] || 'green';
    }

    new Chart(ctx, {
      type: 'sankey',
      options: {
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 24,
            bottom: 24,
          },
        },
      },
      data: {
        datasets: [
          {
            data: [
              ...(this.flows.failed
                ? [
                    {
                      from: 'Recipients',
                      to: 'Send Failed',
                      flow: this.flows.failed,
                    },
                  ]
                : []),
              {
                from: 'Recipients',
                to: 'Sent',
                flow: this.flows.sent,
              },
              ...(this.flows.pending
                ? [
                    {
                      from: 'Recipients',
                      to: 'Pending',
                      flow: this.flows.pending,
                    },
                  ]
                : []),
              ...(this.flows.sentOpened
                ? [
                    {
                      from: 'Sent',
                      to: 'Opened',
                      flow: this.flows.sentOpened,
                    },
                  ]
                : []),
              ...(this.flows.openedUnsubscribed
                ? [
                    {
                      from: 'Opened',
                      to: 'Unsubscribed',
                      flow: this.flows.openedUnsubscribed,
                    },
                  ]
                : []),
              ...(this.flows.openedClicked
                ? [
                    {
                      from: 'Opened',
                      to: 'Clicked',
                      flow: this.flows.openedClicked,
                    },
                  ]
                : []),
              ...(this.flows.sentUnread
                ? [
                    {
                      from: 'Sent',
                      to: 'Unread',
                      flow: this.flows.sentUnread,
                    },
                  ]
                : []),
              ...(this.flows.sentBounced
                ? [
                    {
                      from: 'Sent',
                      to: 'Bounced',
                      flow: this.flows.sentBounced,
                    },
                  ]
                : []),
            ],
            colorFrom: (c) => getColor(c.dataset.data[c.dataIndex].from),
            colorTo: (c) => getColor(c.dataset.data[c.dataIndex].to),
            borderWidth: 0,
            labels,
          },
        ],
      },
    });
  },
};
</script>
